/**
 * One row in the customers list (S09) — list-view variant.
 *
 * Layout matches `prototype/sales/customers.html ▸ .cust-row` exactly:
 *
 *   ┌───┬─────────────────────────┬────────────┬─────────────┬─────────┬─────────┐
 *   │AVA│ name + health pill      │ activity   │ outstanding │ YTD     │ actions │
 *   │ 44│ meta (gstin · city · …) │ what + when│ value + sub │ value+s │ + chev  │
 *   └───┴─────────────────────────┴────────────┴─────────────┴─────────┴─────────┘
 *
 * Plus a 3px LEFT-EDGE COLOURED RAIL keyed off health (rose=late,
 * amber=slow/partial, emerald=on-time, sky=info). The rail is always
 * visible on risky rows (late/warn) and shows on hover for the rest.
 *
 * V1.0.x has the customer master only. Columns that depend on invoices /
 * receipts / AI activity (health, activity, outstanding, YTD) render as
 * `—` placeholders; the wiring lights up automatically when those
 * verticals land — see the inline `// TODO V1.0.x —` markers.
 *
 * Rows live INSIDE one card (`<CustomerListCard>` in this file) — borders
 * are top-borders between rows, not per-row cards, matching the
 * prototype's `.cust-list` grouped surface.
 *
 * @see prototype/sales/customers.html — V1.1 Studio Slate baseline
 */
import { Link } from "@tanstack/react-router";
import { MessageCircle, ChevronRight } from "lucide-react";
import type { customers as contract } from "@flint/api-contract";
import type { z } from "zod";
import { StatusPill, cn } from "@flint/ui";
import { railClass, type RailTone } from "../../master-list/index.ts";
import { paiseToInrShort } from "../../../lib/format.ts";
import { CustomerAvatar } from "./customer-avatar.tsx";

type ListRow = z.infer<typeof contract.listRow>;

export interface CustomerRowProps {
  customer: ListRow;
  /** Extra Tailwind for the row container (e.g. removing the top border on the first row). */
  className?: string;
}

export function CustomerRow({ customer, className }: CustomerRowProps) {
  const isArchived = customer.archivedAt !== null;
  const hasInvoices = customer.invoiceCount > 0;
  const hasOverdue = customer.overdueInvoiceCount > 0;
  const hasOutstanding = BigInt(customer.outstandingPaise) > 0n;
  const rail: RailTone = isArchived
    ? "warn"
    : hasOverdue
      ? "late"
      : hasOutstanding
        ? "warn"
        : "none";
  const lastActivity = customer.lastActivityAt ? shortDate(new Date(customer.lastActivityAt)) : "—";

  return (
    <Link
      to="/sales/customers/$customerId"
      params={{ customerId: customer.id }}
      className={cn(
        "group relative grid items-center gap-3 md:gap-4 px-4 md:px-5 py-3.5 hover:bg-raised transition-colors",
        // Mobile-first: avatar / name+meta / chevron only — readable at 360px.
        "grid-cols-[44px_minmax(0,1fr)_auto]",
        // md (≥768): adds the "Outstanding" column.
        "md:grid-cols-[44px_minmax(0,1fr)_110px_auto]",
        // lg (≥1024): full prototype grid — adds Activity + YTD columns.
        "lg:grid-cols-[44px_minmax(0,1.8fr)_minmax(0,1.2fr)_110px_110px_auto]",
        className,
      )}
    >
      {/* 3px left rail (per prototype `.cust-row::before`). */}
      <span aria-hidden className={railClass(rail)} />

      <CustomerAvatar name={customer.name} size="md" />

      {/* Name + meta */}
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className="text-base font-bold text-ink truncate tracking-tight">
            {customer.name}
          </span>
          {isArchived ? (
            <StatusPill tone="muted" size="xs" dot>
              Archived
            </StatusPill>
          ) : hasOverdue ? (
            <StatusPill tone="bad" size="xs" dot>
              {customer.overdueInvoiceCount} overdue
            </StatusPill>
          ) : hasOutstanding ? (
            <StatusPill tone="warn" size="xs" dot>
              Outstanding
            </StatusPill>
          ) : hasInvoices ? (
            <StatusPill tone="ok" size="xs" dot>
              Clear
            </StatusPill>
          ) : (
            <StatusPill tone="muted" size="xs" dot>
              No data yet
            </StatusPill>
          )}
        </div>
        {/* Meta line: GSTIN · city · contact · history blurb (matches prototype). */}
        <div className="text-xs text-muted truncate leading-snug">
          {customer.gstin ? <span className="font-mono">{customer.gstin}</span> : null}
          {customer.city ? (
            <>
              {customer.gstin ? " · " : ""}
              {customer.city}
            </>
          ) : null}
          {customer.contactName ? (
            <>
              {customer.gstin || customer.city ? " · " : ""}contact {customer.contactName}
            </>
          ) : null}
          {!customer.gstin && !customer.city && !customer.contactName ? (
            <span className="italic">No identity details on file</span>
          ) : null}
        </div>
      </div>

      <div className="hidden lg:flex flex-col gap-1 min-w-0" aria-label="Last activity">
        <span className={cn("text-xs truncate", hasInvoices ? "text-ink-2" : "text-muted italic")}>
          {hasInvoices ? "Invoice issued" : "No activity yet"}
        </span>
        <span className="text-xs text-muted font-mono">{lastActivity}</span>
      </div>

      <div
        className="hidden md:flex flex-col gap-1 text-right tabular-nums"
        aria-label="Outstanding"
      >
        <span
          className={cn(
            "text-base font-bold font-mono",
            hasOutstanding ? "text-ink" : "text-muted",
          )}
        >
          {paiseToInrShort(customer.outstandingPaise)}
        </span>
        <span className="text-xs text-muted">
          {hasInvoices ? `${customer.invoiceCount} inv` : "no invoices"}
        </span>
      </div>

      <div
        className="hidden md:flex flex-col gap-1 text-right tabular-nums"
        aria-label="Billed this financial year"
      >
        <span className="text-sm font-semibold text-ink-2 font-mono">
          {paiseToInrShort(customer.ytdInvoicedPaise)}
        </span>
        <span className="text-xs text-muted">Billed FY · {customer.ytdInvoiceCount} inv</span>
      </div>

      {/* Actions: contextual icon button (placeholder) + chevron */}
      <div className="flex items-center gap-1 justify-end">
        <button
          type="button"
          aria-label="Send WhatsApp reminder"
          title="Reminders wire up with the notifications module"
          disabled
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          className="hidden md:inline-flex items-center justify-center h-8 w-8 rounded-sm border border-line bg-paper text-muted cursor-not-allowed disabled:opacity-50"
        >
          <MessageCircle size={13} strokeWidth={2} aria-hidden />
        </button>
        <span
          aria-hidden
          className="inline-flex items-center justify-center h-6 w-6 text-muted group-hover:text-ink-2 transition-colors"
        >
          <ChevronRight size={14} strokeWidth={2} />
        </span>
      </div>
    </Link>
  );
}

function shortDate(value: Date): string {
  return value.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

// `<CustomerListCard>` was lifted to `features/master-list/MasterListCard`.
// The list page imports it directly from there; this comment exists so a
// reviewer searching for `CustomerListCard` lands on the new home.
